import { AutoFocusExtension, ClearEditorExtension } from "@lexical/extension";
import { HistoryExtension } from "@lexical/history";
import {
  AutoLinkExtension,
  autoLinkEmailMatcher,
  createLinkMatcherWithRegExp,
  LinkExtension,
} from "@lexical/link";
import { ListExtension } from "@lexical/list";
import { $convertToMarkdownString, TRANSFORMERS } from "@lexical/markdown";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { LexicalExtensionComposer } from "@lexical/react/LexicalExtensionComposer";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextExtension } from "@lexical/rich-text";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import TextFormatIcon from "@mui/icons-material/TextFormat";
import { styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import { CLEAR_EDITOR_COMMAND, configExtension, defineExtension, LexicalEditor } from "lexical";
import { useCreateMessageMutation } from "messaging/_api/messaging-api";
import { getMessagingErrorMessage, getMessagingFieldErrors } from "messaging/_api/messaging-error-message";
import { validateAttachment } from "messaging/_api/messaging-validation";
import {
  SEND_MESSAGE_COMMAND,
  SubmitMessagePlugin,
} from "messaging/_views/components/editor/plugins/submit-message-plugin";
import ToolbarPlugin from "messaging/_views/components/editor/plugins/toolbar-plugin";
import { InitialMarkdownPlugin } from "messaging/_views/components/editor/plugins/initial-markdown-plugin";
import Attachment, { FileAttachment } from "messaging/attachments/_views/message-attachment-editor";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import Forum from "messaging/forums/forum";
import Message from "messaging/message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import * as React from "react";
import { useSession } from "users/__session/session-context";
import "./message-editor.css";

const ActionsContainer = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  justifyContent: "space-between",
  marginTop: theme.spacing(0.5),
}));
const OptionButtons = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
}));
const OptionsButtonsContainer = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
}));
const EditorWrapper = styled("div")(() => ({
  position: "relative",
}));
const SendButtonAdornment = styled("div")(() => ({
  alignSelf: "center",
  display: "flex",
  flexShrink: 0,
}));
const SendButton = styled(LoadingButton)(({ theme }) => ({
  lineHeight: 1,
  minHeight: "32px",
  padding: theme.spacing(0.5, 0.75),
  "& .MuiButton-endIcon": {
    marginLeft: theme.spacing(0.75),
  },
}));
const AttachButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
/** Formatting and Attach File, at the start of the field's row, centred on its line as Send is. */
const FieldButtons = styled("div")(({ theme }) => ({
  alignItems: "center",
  alignSelf: "center",
  display: "flex",
  flexShrink: 0,
  marginLeft: theme.spacing(-1),
  marginRight: theme.spacing(0.5),
  "& .MuiToggleButton-root": {
    padding: theme.spacing(0.5),
  },
}));
const AttachmentInput = styled("input")(() => ({
  display: "none",
}));
const AttachmentOptionField = styled("div")(({ theme }) => ({
  alignSelf: "center",
  flex: 1,
  minWidth: 0,
  marginLeft: theme.spacing(1),
  "& .MuiFormControlLabel-label": {
    fontSize: theme.typography.body2.fontSize,
  },
}));

const DEFAULT_PLACEHOLDER = "Type your message here...";

const EditorContentEditable = ({ disabled, placeholder }: { disabled: boolean; placeholder: string }) => {
  return (
    <div className="editor-content">
      <ContentEditable
        aria-placeholder={placeholder}
        aria-disabled={disabled || undefined}
        contentEditable={!disabled}
        placeholder={<div className="placeholder-text">{placeholder}</div>}
      />
    </div>
  );
};

const MESSAGE_LINK_MATCHERS = [
  createLinkMatcherWithRegExp(
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._~#=+]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_~#?&/=+]*)/,
    (text) => (text.startsWith("http") ? text : `https://${text}`),
  ),
  autoLinkEmailMatcher,
];

const messageEditorExtension = defineExtension({
  name: "AttorneyHubMessageEditor",
  namespace: "lexical-editor",
  dependencies: [
    AutoFocusExtension,
    ClearEditorExtension,
    HistoryExtension,
    RichTextExtension,
    ListExtension,
    LinkExtension,
    configExtension(AutoLinkExtension, { matchers: MESSAGE_LINK_MATCHERS }),
  ],
  theme: {
    root: "editor-root",
    quote: "editor-quote",
    link: "editor-link",
    text: {
      bold: "editor-textBold",
      code: "editor-textCode",
      italic: "editor-textItalic",
      strikethrough: "editor-textStrikethrough",
      subscript: "editor-textSubscript",
      superscript: "editor-textSuperscript",
      underline: "editor-textUnderline",
      underlineStrikethrough: "editor-textUnderlineStrikethrough",
    },
  },
  onError: () => undefined,
});

/** What the editor hands a host that posts the message itself. */
export type MessageEditorSubmission = { content: string; file?: File };

/**
 * A choice the editor offers beside an attached file, carried out once the message is sent (#6098): a
 * work agreement's chat offers to add the file to the work's documents too. Unticked until the sender
 * ticks it, and forgotten when the file is removed or sent.
 */
export type SentAttachmentOption = {
  /** What the checkbox says. */
  label: string;
  /** What ticking it means in this conversation, shown under the ticked checkbox. */
  describe: (forum: Forum) => string;
  /** Acts on the sent message's files, as the server stored them, and tells the sender how it went. */
  onSent: (attachments: MessageFileInfo[], forum: Forum) => Promise<void>;
  /** What the sender is told when the choice could not be carried out at all. The message is sent. */
  notDone: string;
};

/**
 * Hands the sent files to the choice the sender ticked. Outside the component, so the send's `try`
 * holds no conditional the React Compiler cannot lower. A ticked choice is never dropped in silence
 * (#6597): with no files to act on, or a failure `onSent` did not report, the sender is told.
 */
function actOnSentAttachments(option: SentAttachmentOption | undefined, forum: Forum | undefined, message: Message) {
  if (!option) return;
  if (!forum || message.attachments.length === 0) {
    enqueueSnackbar(option.notDone, { variant: "warning" });
    return;
  }
  option.onSent(message.attachments, forum).catch(() => enqueueSnackbar(option.notDone, { variant: "error" }));
}

type MessageEditorProps = {
  forum: Forum | undefined;
  autoFocus?: boolean;
  locked?: boolean;
  /** Refuses input, with the placeholder saying why. */
  disabled?: boolean;
  placeholder?: string;
  /** What the submit button says: "Send" on a chat, "Post" on a proposal's comments. */
  submitLabel?: string;
  /**
   * Posts the message instead of the editor: the proposal pane writes a comment through its own
   * comment store rather than to the forum. Answers whether it was posted; the editor keeps the text
   * and the file when it was not.
   */
  onSubmit?: (submission: MessageEditorSubmission) => Promise<boolean>;
  /** Text to start with, as markdown: a draft the host kept while the editor was not mounted. */
  initialText?: string;
  onTextChange?: (text: string) => void;
  /** The reader focused or typed in the editor. */
  onInteraction?: () => void;
  onFormattingToggled?: (enabled: boolean) => void;
  /**
   * Offers Attach File. Defaults to true. A proposal's comments carry text only (#5819), so their composer
   * draws no paperclip and has no file input to reach.
   */
  allowAttachments?: boolean;
  onAttachmentAdded?: (attachment: File) => void;
  onMessageSent?: (message: Message) => void;
  onSendingChange?: (sending: boolean) => void;
  /** Offered beside an attached file, and acted on once the message is sent (#6098). */
  attachmentOption?: SentAttachmentOption;
};

export default function MessageEditor(props: Readonly<MessageEditorProps>) {
  const [isSending, setIsSending] = React.useState(false);
  const onSendingChange = props.onSendingChange;
  React.useEffect(() => { onSendingChange?.(isSending); }, [isSending, onSendingChange]);
  const [messageText, setMessageText] = React.useState<string>("");
  const [subject, setSubject] = React.useState<string | undefined>("");
  const [attachment, setAttachment] = React.useState<FileAttachment | undefined>();
  const [attachmentOptionTicked, setAttachmentOptionTicked] = React.useState(false);
  const [fieldError, setFieldError] = React.useState<string | undefined>();

  const [showToolbar, setShowToolbar] = React.useState(false);
  const editorRef = React.useRef<LexicalEditor>(null);
  const hiddenAttachmentFileInput = React.useRef<HTMLInputElement>(null);
  const session = useSession();
  const [createMessage] = useCreateMessageMutation();
  const submitLabel = props.submitLabel ?? "Send";
  const allowAttachments = props.allowAttachments ?? true;
  const placeholder = props.placeholder ?? DEFAULT_PLACEHOLDER;
  const isDisabled = (): boolean => {
    return Boolean(props.locked || props.disabled || !props.forum || isSending || !session.user);
  };

  const hasVisibleMessageText = (): boolean => /\S/.test(messageText);
  const hasAttachments = (): boolean => attachment !== undefined;
  const messageIsEmpty = (): boolean => !hasVisibleMessageText() && !hasAttachments();
  const canSendMessage = (): boolean => {
    return !isDisabled() && !messageIsEmpty() && !fieldError;
  };
  const emptyMessageTooltip = !isDisabled() && messageIsEmpty() ? "Cannot send an empty message" : "";

  const sendMessage = async (message: Message, editor?: LexicalEditor) => {
    if (!canSendMessage()) return;

    setIsSending(true);
    if (props.onSubmit) {
      const posted = await props.onSubmit({ content: message.content ?? "", file: attachment?.file });
      setIsSending(false);
      if (!posted) return;
      editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      setMessageText("");
      setAttachment(undefined);
      return;
    }
    // Read before the send: the choice belongs to the file being sent, not to whatever is attached next.
    const tickedOption = attachmentOptionTicked ? props.attachmentOption : undefined;
    const forum = props.forum;
    try {
      message.publish();
      const createdMessage = await createMessage({ message }).unwrap();
      props.onMessageSent?.(createdMessage);
      actOnSentAttachments(tickedOption, forum, createdMessage);
      editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      setMessageText("");
      setSubject(undefined);
      setAttachment(undefined);
      setAttachmentOptionTicked(false);
    } catch (error) {
      const fieldErrors = getMessagingFieldErrors(error);
      setFieldError(fieldErrors.contentPlainText ?? fieldErrors.attachments);
      enqueueSnackbar(getMessagingErrorMessage(error, "Failed to send message. Please try again."), {
        variant: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const draftMessage = (messageBody?: string) => {
    if (!props.forum || !session.user || !canSendMessage()) return;
    const attachmentList = attachment ? [attachment.file] : [];
    try {
      return Message.draft({
        id: Guid.generate(),
        created: new AHDate(),
        forum: props.forum,
        sender: session.user,
        content: messageBody ?? messageText,
        subject: subject,
        attachments: attachmentList,
      });
    } catch {
      setFieldError("We could not prepare this message. Check the message and attachment.");
    }
  };

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    const selectedFile = files[0];
    const attachmentError = validateAttachment(selectedFile);
    if (attachmentError) {
      setFieldError(attachmentError);
      event.target.value = "";
      return;
    }

    setAttachment({
      file: selectedFile,
      fileType: selectedFile.type,
      url: URL.createObjectURL(selectedFile),
    });
    setFieldError(undefined);

    event.target.value = "";
    props.onAttachmentAdded?.(selectedFile);
  };

  const handleSendButtonClick = () => {
    if (!canSendMessage()) return;
    const draft = draftMessage();
    if (!draft) return;
    editorRef.current?.dispatchCommand(SEND_MESSAGE_COMMAND, draft);
  };

  const constructMessage = (): Message | undefined => {
    if (messageIsEmpty() || !session.user?.id || !props.forum) return;
    const message = Message.create({
      id: Guid.generate(),
      forum: props.forum,
      content: messageText,
      senderId: session.user.id,
      subject: subject,
    });

    if (!attachment) return message;
    const attachmentFile = new MessageFileInfo({
      id: Guid.generate(),
      name: attachment.file.name,
      fileSize: attachment.file.size,
      mimeType: attachment.fileType,
      createdDate: new AHDate(),
      rawData: attachment.file,
      dataUri: attachment.url,
    });
    message.addAttachments([attachmentFile]);

    return message;
  };

  return (
    <React.Fragment>
      {allowAttachments && (
        <AttachmentInput
          data-testid="attachment-input"
          type="file"
          hidden={true}
          multiple={false}
          ref={hiddenAttachmentFileInput}
          accept={Config.acceptedAttachmentFormats.join(", ")}
          onChange={handleAttachmentUpload}
          style={{ display: "none" }}
        />
      )}
      <EditorWrapper onFocusCapture={() => props.onInteraction?.()}>
        <LexicalExtensionComposer extension={messageEditorExtension} contentEditable={null}>
          <div className="editor-container">
            {showToolbar && <ToolbarPlugin />}
            <div className="editor-inner">
              {/*
               * In the field, beside the text, as Send is at its other end. On a row of their own under
               * the field the two buttons took 38px of a pane short of room for messages.
               */}
              <FieldButtons>
                <Tooltip title="Toggle Formatting">
                  <span>
                    <ToggleButton
                      disabled={isDisabled()}
                      size="small"
                      selected={showToolbar}
                      style={{ border: 0 }}
                      value={showToolbar}
                      onChange={async () => {
                        const nextShowToolbar = !showToolbar;
                        setShowToolbar(nextShowToolbar);
                        props.onFormattingToggled?.(nextShowToolbar);
                      }}
                    >
                      <TextFormatIcon />
                    </ToggleButton>
                  </span>
                </Tooltip>
                {allowAttachments && (
                  <Tooltip title="Attach File">
                    <span>
                      <AttachButton
                        size="small"
                        disabled={attachment !== undefined || isDisabled()}
                        onClick={() => hiddenAttachmentFileInput?.current?.click()}
                      >
                        <AttachFileIcon />
                      </AttachButton>
                    </span>
                  </Tooltip>
                )}
              </FieldButtons>
              <div className="editor-body">
                <EditorContentEditable disabled={isDisabled()} placeholder={placeholder} />
                <InitialMarkdownPlugin markdown={props.initialText} />
                <SubmitMessagePlugin
                  message={constructMessage()}
                  listenForEnterKey={!showToolbar && canSendMessage()}
                  listenForSubmitButton={canSendMessage()}
                  onSend={sendMessage}
                />
                <EditorRefPlugin editorRef={editorRef} />
                <OnChangePlugin
                  ignoreSelectionChange={true}
                  onChange={(editorState) => {
                    editorState.read(() => {
                      const markdown = $convertToMarkdownString(TRANSFORMERS);
                      setMessageText(markdown);
                      setFieldError(undefined);
                      props.onTextChange?.(markdown);
                      if (markdown) props.onInteraction?.();
                    });
                  }}
                />
              </div>
              <SendButtonAdornment>
                <Tooltip title={emptyMessageTooltip}>
                  <span>
                    <SendButton
                      color="primary"
                      size="medium"
                      endIcon={<SendIcon fontSize="medium" />}
                      loading={isSending}
                      disabled={!canSendMessage()}
                      onClick={handleSendButtonClick}
                    >
                      {isSending ? `${submitLabel}ing...` : submitLabel}
                    </SendButton>
                  </span>
                </Tooltip>
              </SendButtonAdornment>
            </div>
          </div>
        </LexicalExtensionComposer>
      </EditorWrapper>
      {(attachment || fieldError) && (
        <ActionsContainer>
          <OptionButtons>
            <OptionsButtonsContainer>
              {attachment?.url && (
                <Attachment
                  attachment={attachment}
                  onAttachmentRemoved={() => {
                    setAttachment(undefined);
                    setAttachmentOptionTicked(false);
                  }}
                />
              )}
              {attachment && props.attachmentOption && props.forum && (
                <AttachmentOptionField>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={attachmentOptionTicked}
                        disabled={isDisabled()}
                        onChange={(event) => setAttachmentOptionTicked(event.target.checked)}
                      />
                    }
                    label={props.attachmentOption.label}
                  />
                  {attachmentOptionTicked && (
                    // It can name the forum (#6330).
                    <FormHelperText className={SESSION_RECORDING_MASK_CLASS}>
                      {props.attachmentOption.describe(props.forum)}
                    </FormHelperText>
                  )}
                </AttachmentOptionField>
              )}
              {fieldError && <div role="alert" className="editor-field-error">{fieldError}</div>}
            </OptionsButtonsContainer>
          </OptionButtons>
        </ActionsContainer>
      )}
    </React.Fragment>
  );
}
